import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatBRL, formatApiError } from "../api";
import { CouponsPanel } from "../components/CouponsPanel";
import { LabelsPanel } from "../components/LabelsPanel";
import { VultLogo } from "../components/Logo";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, Package, ShoppingCart, DollarSign, AlertTriangle, X, Loader2, MessageCircle } from "lucide-react";

const EMPTY_FORM = { name: "", brand: "Nike", gender: "Unissex", price: "", old_price: "", image: "", images: [], badge: "", category: "Streetwear", sizes: "38,39,40,41,42,43,44", colors: "", description: "", in_stock: true, is_featured: false };
const BRANDS = ["Nike", "Jordan", "Adidas", "New Balance", "On", "Mizuno", "Outros"];
const GENDERS = ["Unissex", "Masculino", "Feminino"];
const STATUSES = ["Pendente", "Pago", "Enviado", "Entregue", "Cancelado"];
const STATUS_COLORS = { Pendente: "bg-amber-100 text-amber-700", Pago: "bg-blue-100 text-blue-700", Enviado: "bg-indigo-100 text-indigo-700", Entregue: "bg-emerald-100 text-emerald-700", Cancelado: "bg-red-100 text-red-700" };
const STAGES = ["Pendente", "Saiu da China", "Chegou ao Brasil", "Está no Correios", "Saiu para Entrega"];
const STAGE_COLORS = { Pendente: "bg-amber-100 text-amber-700", "Saiu da China": "bg-sky-100 text-sky-700", "Chegou ao Brasil": "bg-indigo-100 text-indigo-700", "Está no Correios": "bg-violet-100 text-violet-700", "Saiu para Entrega": "bg-emerald-100 text-emerald-700" };

function ProductForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const inputCls = "w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-slate-900";

  const onFile = async (e, gallery = false) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setForm((prev) => gallery
        ? { ...prev, images: [...(prev.images || []), data.url] }
        : { ...prev, image: data.url });
      toast.success(gallery ? "Foto adicionada à galeria" : "Foto enviada com sucesso");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Falha no envio da foto"));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      old_price: form.old_price ? parseFloat(form.old_price) : null,
      sizes: form.sizes.split(",").map((s) => parseInt(s.trim())).filter((n) => !isNaN(n)),
      colors: typeof form.colors === "string" ? form.colors.split(",").map((c) => c.trim()).filter(Boolean) : (form.colors || []),
      badge: form.badge || null,
    };
    try {
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post("/products", payload);
      toast.success(form.id ? "Produto atualizado" : "Produto criado");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <motion.form
        data-testid="vult-admin-product-form"
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="max-h-[90vh] w-full max-w-2xl space-y-3 overflow-y-auto rounded-3xl bg-white p-6 sm:p-8"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl font-black uppercase text-slate-900">{form.id ? "Editar tênis" : "Novo tênis"}</h3>
          <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 hover:bg-slate-900 hover:text-white"><X size={16} /></button>
        </div>
        <input required data-testid="vult-admin-form-name" value={form.name} onChange={set("name")} placeholder="Nome do modelo" className={inputCls} />
        <div className="grid grid-cols-3 gap-3">
          <select data-testid="vult-admin-form-brand" value={form.brand} onChange={set("brand")} className={inputCls}>
            {BRANDS.map((b) => <option key={b}>{b}</option>)}
          </select>
          <select data-testid="vult-admin-form-gender" value={form.gender} onChange={set("gender")} className={inputCls}>
            {GENDERS.map((g) => <option key={g}>{g}</option>)}
          </select>
          <input value={form.category} onChange={set("category")} placeholder="Categoria" className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input required type="number" step="0.01" data-testid="vult-admin-form-price" value={form.price} onChange={set("price")} placeholder="Preço (ex: 899.90)" className={inputCls} />
          <input type="number" step="0.01" value={form.old_price} onChange={set("old_price")} placeholder="Preço antigo (opcional)" className={inputCls} />
        </div>
        <div>
          <input required data-testid="vult-admin-form-image" value={form.image} onChange={set("image")} placeholder="URL da imagem de capa (ou envie uma foto abaixo)" className={inputCls} />
          <div className="mt-2 flex items-center gap-3">
            <label data-testid="vult-admin-form-upload-btn" className="flex cursor-pointer items-center gap-2 rounded-full border-2 border-slate-900 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest transition-colors hover:bg-slate-900 hover:text-white">
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              {uploading ? "Enviando..." : "Enviar foto de capa"}
              <input data-testid="vult-admin-form-file-input" type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e, false)} disabled={uploading} />
            </label>
            {form.image && <img data-testid="vult-admin-form-image-preview" src={form.image} alt="preview" className="h-16 w-16 rounded-xl border border-slate-200 object-cover" />}
          </div>
        </div>
        <div className="rounded-2xl border border-dashed border-slate-300 p-3">
          <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">Galeria de fotos (opcional)</div>
          <div className="flex flex-wrap items-center gap-2">
            {(form.images || []).map((url, i) => (
              <div key={i} className="relative">
                <img src={url} alt={`foto ${i + 1}`} className="h-16 w-16 rounded-xl border border-slate-200 object-cover" />
                <button
                  type="button"
                  data-testid={`vult-admin-gallery-remove-${i}`}
                  onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))}
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-white"
                >
                  <X size={10} />
                </button>
              </div>
            ))}
            <label data-testid="vult-admin-gallery-upload-btn" className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-slate-300 text-slate-400 transition-colors hover:border-slate-900 hover:text-slate-900">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              <input data-testid="vult-admin-gallery-file-input" type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e, true)} disabled={uploading} />
            </label>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input value={form.badge} onChange={set("badge")} placeholder="Badge (ex: Lançamento)" className={inputCls} />
          <input value={form.sizes} onChange={set("sizes")} placeholder="Tamanhos (38,39,40...)" className={inputCls} />
        </div>
        <input data-testid="vult-admin-form-colors" value={Array.isArray(form.colors) ? form.colors.join(",") : form.colors} onChange={set("colors")} placeholder="Cores disponíveis (Preto, Branco, Azul...)" className={inputCls} />
        <textarea value={form.description} onChange={set("description")} placeholder="Descrição" rows={3} className={inputCls} />
        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={form.in_stock} onChange={set("in_stock")} className="h-4 w-4 accent-yellow-500" /> Em estoque</label>
          <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={form.is_featured} onChange={set("is_featured")} className="h-4 w-4 accent-yellow-500" /> Destaque</label>
        </div>
        <button data-testid="vult-admin-form-save" disabled={saving} className="flex w-full items-center justify-center gap-2 rounded-full bg-vult-red py-3.5 font-mono text-sm font-bold uppercase tracking-widest text-slate-900 transition-colors hover:bg-slate-900 hover:text-white disabled:opacity-50">
          {saving && <Loader2 size={15} className="animate-spin" />} Salvar produto
        </button>
      </motion.form>
    </motion.div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("produtos");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false));
  }, []);

  const loadData = async () => {
    try {
      const [s, p, o] = await Promise.all([api.get("/admin/stats"), api.get("/products"), api.get("/orders")]);
      setStats(s.data); setProducts(p.data); setOrders(o.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (user) loadData(); }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("vult_token", data.access_token);
      setUser(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Falha no login"));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("vult_token");
    setUser(false);
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Remover este tênis do catálogo?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Produto removido");
      loadData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}/status`, { status });
      toast.success(`Pedido ${id} → ${status}`);
      loadData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const updateStage = async (id, stage) => {
    try {
      await api.patch(`/orders/${id}/tracking`, { stage });
      toast.success(`Pedido ${id} → ${stage}`);
      loadData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const updateCarrier = async (id, carrier_code) => {
    try {
      await api.patch(`/orders/${id}/tracking`, { carrier_code });
      toast.success(`Pedido ${id} → rastreio Correios salvo`);
      loadData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (user === null) {
    return <div className="flex min-h-screen items-center justify-center bg-white"><Loader2 className="animate-spin text-vult-red" size={32} /></div>;
  }

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8F9FA] px-4">
        <motion.div data-testid="vult-admin-modal" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-8 shadow-[8px_8px_0_#0F172A]">
          <Link to="/"><VultLogo /></Link>
          <h1 className="mt-6 font-display text-3xl font-black uppercase text-slate-900">Painel Admin</h1>
          <p className="mt-1 text-sm text-slate-500">Acesso restrito à equipe Vult.</p>
          <form onSubmit={login} className="mt-6 space-y-3">
            <input data-testid="vult-admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail" className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-slate-900" />
            <input data-testid="vult-admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Senha" className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-slate-900" />
            {error && <div data-testid="vult-admin-login-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm font-medium text-vult-red">{error}</div>}
            <button data-testid="vult-admin-submit-btn" disabled={loggingIn} className="flex w-full items-center justify-center gap-2 rounded-full bg-vult-red py-3.5 font-mono text-sm font-bold uppercase tracking-widest text-slate-900 transition-colors hover:bg-slate-900 hover:text-white disabled:opacity-50">
              {loggingIn && <Loader2 size={15} className="animate-spin" />} Entrar
            </button>
          </form>
          <Link to="/" className="mt-4 block text-center font-mono text-[11px] font-bold uppercase tracking-widest text-slate-400 hover:text-slate-900">← Voltar à loja</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="vult-admin-dashboard-view" className="min-h-screen bg-[#F8F9FA]">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/"><VultLogo size={30} /></Link>
          <div className="flex items-center gap-3">
            <span className="hidden font-mono text-xs font-bold uppercase tracking-widest text-slate-500 sm:block">{user.email}</span>
            <button data-testid="vult-admin-logout-btn" onClick={logout} className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest transition-colors hover:border-vult-red hover:text-vult-red">
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <h1 className="font-display text-4xl font-black uppercase text-slate-900">Dashboard</h1>

        {stats && (
          <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[
              { label: "Vendas totais", value: formatBRL(stats.vendas_totais), icon: DollarSign, color: "text-emerald-600" },
              { label: "Pedidos ativos", value: stats.pedidos_ativos, icon: ShoppingCart, color: "text-vult-blue" },
              { label: "Produtos", value: stats.produtos_total, icon: Package, color: "text-slate-900" },
              { label: "Sem estoque", value: stats.estoque_baixo, icon: AlertTriangle, color: "text-vult-red" },
            ].map((c) => (
              <div key={c.label} data-testid={`vult-admin-stat-${c.label.toLowerCase().replace(/\s/g, "-")}`} className="rounded-2xl border border-slate-200 bg-white p-5">
                <c.icon size={20} className={c.color} />
                <div className="mt-3 font-display text-3xl font-black text-slate-900">{c.value}</div>
                <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">{c.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <div className="flex gap-2">
            {["produtos", "pedidos", "cupons", "etiquetas"].map((t) => (
              <button
                key={t}
                data-testid={`vult-admin-tab-${t}`}
                onClick={() => setTab(t)}
                className={`rounded-full px-5 py-2 font-mono text-xs font-bold uppercase tracking-widest transition-all ${tab === t ? "bg-slate-900 text-white shadow-[3px_3px_0_#FFD600]" : "border border-slate-300 bg-white text-slate-600"}`}
              >
                {t}
              </button>
            ))}
          </div>
          {tab === "produtos" && (
            <button
              data-testid="vult-admin-add-product-btn"
              onClick={() => setEditing({ ...EMPTY_FORM })}
              className="flex items-center gap-2 rounded-full bg-vult-red px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-widest text-slate-900 transition-colors hover:bg-slate-900 hover:text-white"
            >
              <Plus size={14} /> Novo tênis
            </button>
          )}
        </div>

        {tab === "produtos" && (
          <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-4">Produto</th><th className="px-5 py-4">Marca</th><th className="px-5 py-4">Preço</th><th className="px-5 py-4">Estoque</th><th className="px-5 py-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} data-testid={`vult-admin-product-row-${p.id}`} className="border-b border-slate-100 last:border-0 hover:bg-[#F8F9FA]">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <img src={p.image} alt="" className="h-12 w-12 rounded-lg object-cover" />
                        <span className="font-display text-base font-bold uppercase text-slate-900">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs font-bold uppercase text-slate-600">{p.brand}</td>
                    <td className="px-5 py-3 font-bold">{formatBRL(p.price)}</td>
                    <td className="px-5 py-3">
                      <span className={`rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase ${p.in_stock ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {p.in_stock ? "Ativo" : "Esgotado"}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-2">
                        <button data-testid={`vult-admin-edit-${p.id}`} onClick={() => setEditing({ ...p, sizes: p.sizes.join(","), old_price: p.old_price || "", badge: p.badge || "" })} className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-900 hover:text-white"><Pencil size={14} /></button>
                        <button data-testid={`vult-admin-delete-${p.id}`} onClick={() => deleteProduct(p.id)} className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-vult-red hover:text-white"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "pedidos" && (
          <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            {orders.length === 0 ? (
              <div data-testid="vult-admin-orders-empty" className="py-16 text-center font-display text-xl font-black uppercase text-slate-300">Nenhum pedido ainda</div>
            ) : (
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    <th className="px-5 py-4">Pedido</th><th className="px-5 py-4">Cliente</th><th className="px-5 py-4">Itens</th><th className="px-5 py-4">Total</th><th className="px-5 py-4">Pagamento</th><th className="px-5 py-4">Status</th><th className="px-5 py-4">Andamento</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} data-testid={`vult-admin-order-row-${o.id}`} className="border-b border-slate-100 last:border-0 hover:bg-[#F8F9FA]">
                      <td className="px-5 py-3 font-mono text-xs font-bold">{o.id}</td>
                      <td className="px-5 py-3">
                        <div className="font-bold text-slate-900">{o.customer_name}</div>
                        <div className="text-xs text-slate-500">{o.customer_email}</div>
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-600">{o.items.map((i) => `${i.qty}x ${i.name} (Tam ${i.size}${i.color ? `, ${i.color}` : ""})`).join(" | ")}</td>
                      <td className="px-5 py-3 font-bold">{formatBRL(o.total)}</td>
                      <td className="px-5 py-3 font-mono text-xs font-bold uppercase">{o.payment_method}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <select
                            data-testid={`vult-admin-order-status-${o.id}`}
                            value={o.status}
                            onChange={(e) => updateStatus(o.id, e.target.value)}
                            className={`rounded-full px-3 py-1.5 font-mono text-[10px] font-bold uppercase outline-none ${STATUS_COLORS[o.status] || ""}`}
                          >
                            {STATUSES.map((s) => <option key={s}>{s}</option>)}
                          </select>
                          {o.customer_phone && (
                            <a
                              data-testid={`vult-admin-whatsapp-${o.id}`}
                              href={`https://wa.me/55${String(o.customer_phone).replace(/\D/g, "")}?text=${encodeURIComponent(`Olá, ${o.customer_name.split(" ")[0]}, obrigado por adquirir conosco! Seu pedido ${o.id} na Vult está com status: ${o.status}.${o.carrier_code ? ` Rastreio Correios: ${o.carrier_code}.` : ""} Acompanhe seu pedido: ${window.location.origin}/pedido/${o.id}. Prazo estimado: 20 dias até o destino final.`)}`}
                              target="_blank"
                              rel="noreferrer"
                              title="Avisar cliente no WhatsApp"
                              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white transition-colors hover:bg-emerald-600"
                            >
                              <MessageCircle size={15} />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <select
                          data-testid={`vult-admin-order-stage-${o.id}`}
                          value={o.tracking_stage || "Pendente"}
                          onChange={(e) => updateStage(o.id, e.target.value)}
                          className={`rounded-full px-3 py-1.5 font-mono text-[10px] font-bold uppercase outline-none ${STAGE_COLORS[o.tracking_stage || "Pendente"] || ""}`}
                        >
                          {STAGES.map((s) => <option key={s}>{s}</option>)}
                        </select>
                        <input
                          data-testid={`vult-admin-order-carrier-${o.id}`}
                          defaultValue={o.carrier_code || ""}
                          placeholder="Cód. Correios (Enter salva)"
                          onKeyDown={(e) => { if (e.key === "Enter") updateCarrier(o.id, e.target.value); }}
                          onBlur={(e) => { if (e.target.value.trim().toUpperCase() !== (o.carrier_code || "")) updateCarrier(o.id, e.target.value); }}
                          className="mt-2 w-40 rounded-full border border-slate-200 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider outline-none transition-colors focus:border-slate-900"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {tab === "cupons" && <CouponsPanel />}
        {tab === "etiquetas" && <LabelsPanel />}
      </main>

      <AnimatePresence>
        {editing && <ProductForm initial={editing} onClose={() => setEditing(null)} onSaved={loadData} />}
      </AnimatePresence>
    </div>
  );
}
